/**
 * Icon set for the journey map's events.
 *
 * Every icon is a 24-unit square drawn in currentColor, with a second tone
 * made by lowering opacity on the recessed parts. Shapes are heavy and
 * rounded so they stay legible at 24px on a coloured stone and still look
 * carved rather than printed at 40px.
 */
import type {SVGProps} from 'react';

/** Props shared by every journey icon. */
export interface JourneyIconProps extends SVGProps<SVGSVGElement> {
  /** Rendered width and height in pixels. Defaults to 24. */
  size?: number;
}

function Svg({size = 24, children, ...rest}: JourneyIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

/** An open book: a lesson to read or watch. */
export function LearnIcon(props: JourneyIconProps) {
  return (
    <Svg {...props}>
      <path d="M2 5.2c0-.8.6-1.5 1.4-1.6 2.7-.4 5.4.1 7.6 1.6v14.6c-2.2-1.3-4.9-1.7-7.6-1.4-.8.1-1.4-.6-1.4-1.4z" />
      <path
        d="M22 5.2c0-.8-.6-1.5-1.4-1.6-2.7-.4-5.4.1-7.6 1.6v14.6c2.2-1.3 4.9-1.7 7.6-1.4.8.1 1.4-.6 1.4-1.4z"
        opacity={0.78}
      />
      <path
        d="M4.6 8.1c1.5-.1 3 .1 4.3.6M4.6 11.2c1.5-.1 3 .1 4.3.6M15.1 8.7c1.3-.5 2.8-.7 4.3-.6M15.1 11.8c1.3-.5 2.8-.7 4.3-.6"
        stroke="currentColor"
        strokeWidth={1.4}
        strokeLinecap="round"
        fill="none"
        opacity={0.5}
      />
      <path d="M11 5.2h2v14.6h-2z" opacity={0.35} />
    </Svg>
  );
}

/** Angle brackets with a slash: an assignment to code. */
export function CodeIcon(props: JourneyIconProps) {
  return (
    <Svg {...props}>
      <path
        d="M7.5 6.5 2.8 12l4.7 5.5M16.5 6.5l4.7 5.5-4.7 5.5"
        stroke="currentColor"
        strokeWidth={3}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M13.6 4.2 10.4 19.8"
        stroke="currentColor"
        strokeWidth={3}
        strokeLinecap="round"
        fill="none"
        opacity={0.7}
      />
    </Svg>
  );
}

/** A faceted gem: an optional bonus off the trail. */
export function GemIcon(props: JourneyIconProps) {
  return (
    <Svg {...props}>
      <path d="M2.2 9.2 12 21.5 21.8 9.2z" opacity={0.7} />
      <path d="M8.6 9.2h6.8L12 21.5z" opacity={0.9} />
      <path d="M6.6 3h10.8l4.4 6.2H2.2z" />
      <path d="M8.6 9.2 6.6 3l-4.4 6.2z" opacity={0.85} />
      <path d="M15.4 9.2 17.4 3l4.4 6.2z" opacity={0.85} />
    </Svg>
  );
}

/** A waving flag on a pole: the start of a week. */
export function FlagIcon(props: JourneyIconProps) {
  return (
    <Svg {...props}>
      <path
        d="M4.5 2.5a1.5 1.5 0 0 1 1.5 1.5v18h-3V4a1.5 1.5 0 0 1 1.5-1.5z"
        opacity={0.7}
      />
      <path d="M6 4.2c2.4-1.4 4.6-1.4 7 0s4.6 1.4 7.5 0v9.3c-2.9 1.4-5.1 1.4-7.5 0s-4.6-1.4-7 0z" />
      <path
        d="M13 4.2c-2.4-1.4-4.6-1.4-7 0v4.7c2.4-1.4 4.6-1.4 7 0z"
        opacity={0.3}
        fill="#fff"
      />
    </Svg>
  );
}

/** A trophy cup: a week completed. */
export function TrophyIcon(props: JourneyIconProps) {
  return (
    <Svg {...props}>
      <path d="M7 3h10a1 1 0 0 1 1 1v1h2.5a1 1 0 0 1 1 1c0 3.1-1.9 5.6-4.7 6.5A5.6 5.6 0 0 1 13.5 15v2H16a1 1 0 0 1 1 1v2.5H7V18a1 1 0 0 1 1-1h2.5v-2a5.6 5.6 0 0 1-3.3-2.5C4.4 11.6 2.5 9.1 2.5 6a1 1 0 0 1 1-1H6V4a1 1 0 0 1 1-1z" />
      <path
        d="M4.6 7c.2 1.6 1 2.9 2.2 3.7A10 10 0 0 1 6 7zm14.8 0h-.8a10 10 0 0 1-.8 3.7c1.2-.8 2-2.1 2.2-3.7z"
        opacity={0.35}
        fill="#fff"
      />
      <path d="M9.5 5.5h2v6h-2z" opacity={0.3} fill="#fff" />
    </Svg>
  );
}

/** A padlock: not yet available. */
export function LockIcon(props: JourneyIconProps) {
  return (
    <Svg {...props}>
      <path
        d="M12 2.5a5 5 0 0 1 5 5V10h-2.4V7.5a2.6 2.6 0 0 0-5.2 0V10H7V7.5a5 5 0 0 1 5-5z"
        opacity={0.75}
      />
      <path d="M6 10h12a2 2 0 0 1 2 2v7.5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V12a2 2 0 0 1 2-2z" />
      <path
        d="M12 13.5a1.6 1.6 0 0 1 .9 2.9V18h-1.8v-1.6a1.6 1.6 0 0 1 .9-2.9z"
        opacity={0.4}
        fill="#000"
      />
    </Svg>
  );
}

/** A heavy check mark: done. */
export function CheckIcon(props: JourneyIconProps) {
  return (
    <Svg {...props}>
      <path
        d="M4.5 12.8 9.4 17.6 19.5 7"
        stroke="currentColor"
        strokeWidth={3.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </Svg>
  );
}

/** A five-point star: the badge on a completed stone. */
export function StarIcon(props: JourneyIconProps) {
  return (
    <Svg {...props}>
      <path d="M12 2.6c.4 0 .7.2.9.6l2.5 5.2 5.7.8c.8.1 1.1 1.1.5 1.7l-4.1 4 1 5.7c.1.8-.7 1.4-1.4 1L12 18.9l-5.1 2.7c-.7.4-1.5-.2-1.4-1l1-5.7-4.1-4c-.6-.6-.3-1.6.5-1.7l5.7-.8 2.5-5.2c.2-.4.5-.6.9-.6z" />
    </Svg>
  );
}

/** Two figures side by side: a section meeting. */
export function SectionIcon(props: JourneyIconProps) {
  return (
    <Svg {...props}>
      <circle cx={8.5} cy={8} r={3.6} />
      <path d="M2.2 19.5c0-3.6 2.8-6.2 6.3-6.2s6.3 2.6 6.3 6.2a1 1 0 0 1-1 1H3.2a1 1 0 0 1-1-1z" />
      <circle cx={16.5} cy={8.6} r={3} opacity={0.75} />
      <path
        d="M15.4 13.4c3.3.2 5.9 2.7 5.9 6.1a1 1 0 0 1-1 1h-4.1c.1-2.9-.9-5.2-2.6-6.6.6-.3 1.2-.5 1.8-.5z"
        opacity={0.75}
      />
    </Svg>
  );
}

/** An arrow leaving a box: an external link. */
export function LinkIcon(props: JourneyIconProps) {
  return (
    <Svg {...props}>
      <path
        d="M10 5H6.5A2.5 2.5 0 0 0 4 7.5v10A2.5 2.5 0 0 0 6.5 20h10a2.5 2.5 0 0 0 2.5-2.5V14"
        stroke="currentColor"
        strokeWidth={2.6}
        strokeLinecap="round"
        fill="none"
        opacity={0.75}
      />
      <path
        d="M14 4h6v6M19.5 4.5 11 13"
        stroke="currentColor"
        strokeWidth={2.8}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </Svg>
  );
}
